/**
 * Requests-tab vocabulary (W4a — coordination-tab council rulings A.1 + A.2).
 *
 * The single source for the operator-facing copy of the unified "Requests"
 * coordination tab: its tab label, the two DIRECTED section headings, and the
 * per-row "From <originator>" / "To <target agency>" direction+party prefixes.
 *
 * Doctrine (council A.1/A.2, coordination-rearch Ruling 2, IMS §5.4 up/down):
 * inbound (`assistance_requests`, municipality/First-Nation → PEOC) and outbound
 * (`agency_tasks`, PEOC → agency) are doctrinally distinct flows — one shared tab
 * for declutter (design-council PART IX), NEVER one merged list. Every row carries
 * an explicit DIRECTION + PARTY; direction DERIVES from the source table, never a
 * column. The handling desk (if surfaced) reads "Claimed by" / "Assigned to" —
 * NEVER "owned by"/"owner"/"owns" ("Owned" is the signed §4.3 Incident-Command
 * handle, reserved). All copy here is descriptive-no-collision Ontario IMS.
 *
 * Kept in its own module (no React import) so the pinned vocabulary lock
 * (requestsTabVocabulary.lock.test.ts) can scan it — plus the shared status /
 * urgency label maps in AssistQueueList.ts + agencyTaskUi.ts — without importing
 * the panel component tree. Same idiom as incidentVocabulary.ts + its lock test.
 */

/** Primary tab label for the unified coordination Requests tab (Inbound + Outbound). */
export const REQUESTS_TAB_LABEL = 'Requests';

/** The drone-marketplace tab, renamed off the freed "Requests" name (A.1 collision fix). */
export const DRONE_REQUESTS_TAB_LABEL = 'Drone requests';

/**
 * The requestor-side tray label (#419 / #565): this desk's OWN submitted
 * requests-for-assistance (the `assistance_requests` this seat raised, mirrored back
 * to the requestor). Renamed off the ambiguous first-person "My Requests" (founder
 * finding 2026-07-06) to the descriptive "Sent requests" — the frame is the honest
 * direction (what this seat SENT), not a possessive. "Sent" reuses the shipped
 * out-of-app honesty verb (the interops `sentAtLabel` / briefing surface: recorded as
 * sent, never a fabricated onward acknowledgement). Doctrine: a seat's request for
 * assistance (App A #46 liaison-officer "requests for assistance" / PERP RFA §6.7).
 * The sub-tab id `my-requests` is UNCHANGED — a backend id, not an operator label.
 */
export const SENT_REQUESTS_TAB_LABEL = 'Sent requests';

/** Directed section headings inside the one Requests tab. */
export const REQUESTS_INBOUND_HEADING = 'Inbound';
export const REQUESTS_OUTBOUND_HEADING = 'Outbound';

/** One-line direction+party subtext under each section heading. */
export const REQUESTS_INBOUND_SUBTEXT =
  'Assistance requests from municipalities and First Nations to the PEOC.';
export const REQUESTS_OUTBOUND_SUBTEXT = 'Tasks the PEOC issued to a named agency.';

/**
 * INBOUND row prefix (A.2): every inbound Requests row reads "From <originator>"
 * — the `assistance_requests.requestor_name`. Direction is inferred from the
 * source table (assistance_requests = inbound), not stored on the row.
 */
export function inboundRequestorLabel(requestorName: string): string {
  return `From ${requestorName}`;
}

/**
 * OUTBOUND row prefix (A.2): every outbound Requests row reads "To <target agency>"
 * — the `agency_tasks.target_agency_id`'s resolved name. Direction is inferred from
 * the source table (agency_tasks = outbound), not stored on the row. Falls back to a
 * generic "Agency" when the join name is absent (mirrors the panel's prior fallback).
 */
export function outboundTargetLabel(targetAgencyName: string | null | undefined): string {
  return `To ${targetAgencyName ?? 'Agency'}`;
}
